import { Fragment, useState, useEffect, useMemo } from 'react';
import { ApiFout, api } from '@/lib/api';
import type { VerbruikRegel, VerbruikTarieven } from '@/lib/api-types';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Download, Search, List, ChevronLeft, ChevronRight, ChevronDown, AlertTriangle } from 'lucide-react';

/**
 * Eén verbruiksregel. Gelijk aan VerbruikRegel uit api-types; de alias blijft omdat
 * de rest van dit bestand hem zo noemt.
 */
type DetailedLog = VerbruikRegel;

const USAGE_TYPE_LABELS: Record<string, string> = {
  speech_recording: 'Spraakopname',
  soep_transcription: 'SOEP Samenvatting',
  translation: 'Vertaling',
  icpc_suggestion: 'ICPC Suggestie',
  patient_summary: 'Patiëntsamenvatting (PDF)',
  acs_call: 'Telefoongesprek (ACS)',
};

function formatDuration(totalSeconds: number): string {
  const mins = Math.floor(totalSeconds / 60);
  const secs = Math.round(totalSeconds % 60);
  if (mins === 0) return `${secs}s`;
  return `${mins}m ${secs.toString().padStart(2, '0')}s`;
}

function formatCost(cost: number | null): string {
  if (cost === null || cost === undefined) return '-';
  return `€${cost.toFixed(4).replace('.', ',')}`;
}

/**
 * Zes decimalen, want daarop rondt de API de bedragen af (numeric(12,6)) en
 * daarmee valt de uitkomst na te rekenen. De vier decimalen van formatCost zijn
 * voor de tabel zelf; die leest prettiger maar verbergt het verschil.
 */
function formatBedrag(waarde: number | null | undefined): string {
  if (waarde === null || waarde === undefined || !Number.isFinite(waarde)) return '—';
  return `€${waarde.toFixed(6).replace('.', ',')}`;
}

function formatDatum(datum: string | null): string {
  if (!datum) return 'onbepaald';
  const [jaar, maand, dag] = datum.split('-');
  return dag ? `${dag}-${maand}-${jaar}` : datum;
}

/**
 * Waarmee twee bedragen "gelijk" zijn.
 *
 * De API geeft de kosten op zes decimalen; de narekening hieronder rekent met de
 * onafgeronde tarieven. Een verschil kleiner dan de afronding zelf is dus geen
 * afwijking.
 */
const AFRONDING = 1e-6;

/** Regels per pagina in de tabel. */
const PAGINAGROOTTE = 100;

/** Regels per verzoek bij de export: het maximum dat de API toestaat. */
const EXPORTPAGINA = 2000;

/** Hoe lang na de laatste toetsaanslag de zoekterm naar de server gaat. */
const ZOEKVERTRAGING_MS = 300;

const MONTH_NAMES = ['Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni', 'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'];

/** Het antwoord van GET /api/admin/usage/entries. */
interface RegelsAntwoord {
  entries: VerbruikRegel[];
  /** Het aantal regels in de hele selectie. */
  total: number;
  /** Inkoop en verkoop over de hele selectie. */
  totals: { purchase: number; selling: number };
  /** Waar de volgende pagina begint; null na de laatste. */
  nextCursor: string | null;
}

/**
 * De selectie als query-parameters.
 *
 * Het filteren gebeurt op de server. Toen het in de browser gebeurde, filterde het
 * alleen de 500 regels die er geladen waren: wie een maand van vorig jaar koos,
 * kreeg een lege of halve maand te zien en exporteerde die ook zo.
 */
function selectieQuery(jaar: string, maand: string, zoekterm: string) {
  const term = zoekterm.trim();
  // De leesbare namen bestaan alleen hier. Welke typen een naam hebben waar de
  // term in staat, rekent het scherm dus zelf uit; de server zoekt daarnaast op
  // de technische naam.
  const typen = term
    ? Object.entries(USAGE_TYPE_LABELS)
        .filter(([, naam]) => naam.toLowerCase().includes(term.toLowerCase()))
        .map(([type]) => type)
    : [];

  return {
    year: jaar === 'all' ? undefined : Number(jaar),
    // De keuzelijst telt vanaf nul, zoals Date.getMonth(); de API vanaf één.
    month: maand === 'all' ? undefined : Number(maand) + 1,
    search: term || undefined,
    searchTypes: typen.length > 0 ? typen.join(',') : undefined,
  };
}

export function UsageLogsTab() {
  const [logs, setLogs] = useState<DetailedLog[]>([]);
  const [total, setTotal] = useState(0);
  const [totals, setTotals] = useState({ purchase: 0, selling: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  /** De zoekterm zoals hij naar de server gaat: pas als er even niet getypt wordt. */
  const [zoekfilter, setZoekfilter] = useState('');
  const [selectedYear, setSelectedYear] = useState<string>('all');
  const [selectedMonth, setSelectedMonth] = useState<string>('all');
  const [pagina, setPagina] = useState(0);
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  /** Hoeveel regels de export al binnen heeft; null als er geen export loopt. */
  const [exportGeladen, setExportGeladen] = useState<number | null>(null);
  /** Welke regel is uitgeklapt. Eén tegelijk: de tabel is al dicht bezet. */
  const [openId, setOpenId] = useState<string | null>(null);

  const selectie = useMemo(
    () => selectieQuery(selectedYear, selectedMonth, zoekfilter),
    [selectedYear, selectedMonth, zoekfilter],
  );

  useEffect(() => {
    void (async () => {
      try {
        const { years } = await api.get<{ years: number[] }>('/admin/usage/years');
        setAvailableYears(years ?? []);
      } catch {
        // Zonder jarenlijst blijft "Alle jaren" over; de regels zelf laden wel.
      }
    })();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setZoekfilter(searchTerm);
      setPagina(0);
    }, ZOEKVERTRAGING_MS);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    // Een antwoord op een selectie die alweer veranderd is, hoort niet in de tabel.
    const afbreken = new AbortController();
    setIsLoading(true);
    void (async () => {
      try {
        // GET /api/admin/usage/entries vereist een beheerder en geeft per verzoek
        // hooguit 2000 regels. get-practice-usage had geen controle en geen limiet:
        // met detailed_logs:true kwam het hele logboek mee.
        const antwoord = await api.get<RegelsAntwoord>('/admin/usage/entries', {
          query: { ...selectie, limit: PAGINAGROOTTE, offset: pagina * PAGINAGROOTTE },
          signal: afbreken.signal,
        });
        setLogs(antwoord.entries);
        setTotal(antwoord.total);
        setTotals(antwoord.totals);
        setOpenId(null);
      } catch (fout) {
        if (afbreken.signal.aborted) return;
        toast.error(fout instanceof ApiFout ? fout.message : 'Kon het verbruikslog niet laden');
      } finally {
        if (!afbreken.signal.aborted) setIsLoading(false);
      }
    })();
    return () => afbreken.abort();
  }, [selectie, pagina]);

  const aantalPaginas = Math.max(1, Math.ceil(total / PAGINAGROOTTE));
  const eersteRegel = total === 0 ? 0 : pagina * PAGINAGROOTTE + 1;
  const laatsteRegel = Math.min(total, (pagina + 1) * PAGINAGROOTTE);

  /**
   * Alle regels van de selectie, pagina voor pagina.
   *
   * Met de cursor en niet met offset: het logboek loopt door terwijl de export
   * ophaalt, en een nieuwe regel bovenaan schuift bij offset alles een plek op.
   */
  const haalSelectieOp = async (): Promise<DetailedLog[]> => {
    const alle: DetailedLog[] = [];
    let cursor: string | undefined;
    do {
      const antwoord = await api.get<RegelsAntwoord>('/admin/usage/entries', {
        query: { ...selectie, limit: EXPORTPAGINA, cursor },
      });
      alle.push(...antwoord.entries);
      setExportGeladen(alle.length);
      cursor = antwoord.nextCursor ?? undefined;
    } while (cursor);
    return alle;
  };

  const handleExport = async () => {
    setExportGeladen(0);
    let regels: DetailedLog[];
    try {
      regels = await haalSelectieOp();
    } catch (fout) {
      toast.error(fout instanceof ApiFout ? fout.message : 'Kon de export niet ophalen');
      return;
    } finally {
      setExportGeladen(null);
    }
    downloadCsv(regels);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <List className="h-5 w-5" />
          Gedetailleerd Verbruiksoverzicht
        </CardTitle>
        <CardDescription>
          Alle AI- en spraakservice-aanroepen met inkoop- en verkoopkosten
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Zoek op praktijk, gebruiker of type..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select
            value={selectedYear}
            onValueChange={(waarde) => {
              setSelectedYear(waarde);
              setPagina(0);
            }}
          >
            <SelectTrigger className="w-[120px]">
              <SelectValue placeholder="Jaar" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle jaren</SelectItem>
              {availableYears.map(y => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={selectedMonth}
            onValueChange={(waarde) => {
              setSelectedMonth(waarde);
              setPagina(0);
            }}
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Maand" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle maanden</SelectItem>
              {MONTH_NAMES.map((name, i) => (
                <SelectItem key={i} value={String(i)}>{name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void handleExport()}
            disabled={total === 0 || exportGeladen !== null}
          >
            {exportGeladen === null ? (
              <Download className="h-4 w-4 mr-1" />
            ) : (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            )}
            {exportGeladen === null ? 'Excel' : `Excel (${exportGeladen} van ${total})`}
          </Button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <div className="text-xs text-muted-foreground">
              {total} regels · Inkoop: {formatCost(totals.purchase)} · Verkoop: {formatCost(totals.selling)}
            </div>
            <div className="border rounded-md overflow-hidden">
              <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0">
                    <tr className="bg-muted">
                      <th className="w-6 p-2" aria-label="Uitklappen" />
                      <th className="text-left p-2 font-medium">Praktijk</th>
                      <th className="text-left p-2 font-medium">Gebruiker</th>
                      <th className="text-left p-2 font-medium">Type</th>
                      <th className="text-left p-2 font-medium">AI Model</th>
                      <th className="text-right p-2 font-medium">Duur</th>
                      <th className="text-left p-2 font-medium">Datum</th>
                      <th className="text-left p-2 font-medium">Tijd</th>
                      <th className="text-right p-2 font-medium">Inkoop</th>
                      <th className="text-right p-2 font-medium">Verkoop</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="p-4 text-center text-muted-foreground">
                          Geen verbruiksdata gevonden
                        </td>
                      </tr>
                    ) : (
                      logs.map((log) => {
                        const dt = new Date(log.createdAt);
                        const isOpen = openId === log.id;
                        const klapUit = () => setOpenId(isOpen ? null : log.id);

                        return (
                          <Fragment key={log.id}>
                          <tr
                            className="border-t hover:bg-muted/50 cursor-pointer"
                            // aria-expanded hoort bij een rij die open kan; met
                            // tabIndex en Enter/spatie werkt het ook zonder muis.
                            aria-expanded={isOpen}
                            tabIndex={0}
                            onClick={klapUit}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                klapUit();
                              }
                            }}
                          >
                            <td className="p-2 text-muted-foreground">
                              {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                            </td>
                            <td className="p-2">{log.practiceName}</td>
                            <td className="p-2">{log.userName}</td>
                            <td className="p-2">{USAGE_TYPE_LABELS[log.usageType] || log.usageType}</td>
                            <td className="p-2 text-muted-foreground">{log.model || '-'}</td>
                            <td className="p-2 text-right font-mono">{log.usageType === 'speech_recording' || log.usageType === 'acs_call' ? formatDuration(log.seconds) : '-'}</td>
                            <td className="p-2">{dt.toLocaleDateString('nl-NL')}</td>
                            <td className="p-2">{dt.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</td>
                            <td className="p-2 text-right font-mono">{formatCost(log.purchaseCost)}</td>
                            <td className="p-2 text-right font-mono">{formatCost(log.sellingCost)}</td>
                          </tr>
                          {isOpen && (
                            <tr className="border-t bg-muted/30">
                              <td />
                              <td colSpan={9} className="p-3">
                                <Narekening log={log} />
                              </td>
                            </tr>
                          )}
                          </Fragment>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            {total > PAGINAGROOTTE && (
              <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                <span>
                  Regels {eersteRegel}–{laatsteRegel} van {total}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPagina(pagina - 1)}
                    disabled={pagina === 0}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Vorige
                  </Button>
                  <span>
                    Pagina {pagina + 1} van {aantalPaginas}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPagina(pagina + 1)}
                    disabled={pagina + 1 >= aantalPaginas}
                  >
                    Volgende
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

/** Zet de regels in een CSV-bestand en laat de browser het downloaden. */
function downloadCsv(regels: DetailedLog[]): void {
  const sep = ';';
  // De tarievenkolommen staan achteraan en niet tussen de bestaande. Wie dit
  // bestand in een sjabloon inleest rekent op de kolomvolgorde die er was;
  // achteraan bijplakken laat die staan. Dezelfde gegevens als de drilldown, maar
  // dan voor de hele selectie in één keer.
  const header = [
    'Praktijknaam', 'Gebruiker', 'Type', 'AI Model', 'Duur', 'Datum', 'Tijd', 'Seconden',
    'InkoopExBtw', 'VerkoopExBtw',
    'Basis', 'PeriodeStart', 'PeriodeEind', 'InkoopPerSec', 'VerkoopPerSec', 'MargePct', 'InputCost',
  ].join(sep);

  /** Leeg blijft leeg: een ontbrekend tarief is geen nul. */
  const getal = (waarde: number | null, decimalen: number): string =>
    waarde === null ? '' : waarde.toFixed(decimalen).replace('.', ',');

  const rows = regels.map(log => {
    const dt = new Date(log.createdAt);
    // Zelfde voorbehoud als in Narekening: een API die ouder is dan dit scherm
    // stuurt de tarieven niet mee. De kolommen blijven dan leeg; een export mag
    // daar niet op stuklopen.
    const t = log.pricing as VerbruikTarieven | undefined;
    return [
      log.practiceName,
      log.userName,
      USAGE_TYPE_LABELS[log.usageType] || log.usageType,
      log.model || '',
      log.usageType === 'speech_recording' || log.usageType === 'acs_call' ? formatDuration(log.seconds) : '',
      dt.toLocaleDateString('nl-NL'),
      dt.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
      log.seconds.toString(),
      (log.purchaseCost ?? 0).toFixed(6).replace('.', ','),
      (log.sellingCost ?? 0).toFixed(6).replace('.', ','),
      t === undefined ? '' : t.basis === 'seconds' ? 'seconden' : 'input_cost',
      t?.billingPeriodStart ? formatDatum(t.billingPeriodStart) : '',
      t?.billingPeriodId ? formatDatum(t.billingPeriodEnd) : '',
      getal(t?.voicePurchasePricePerSecond ?? null, 6),
      getal(t?.voiceSellingPricePerSecond ?? null, 6),
      getal(t?.llmMarginPercentage ?? null, 2),
      getal(t?.inputCost ?? null, 6),
    ].join(sep);
  });

  const bom = '﻿';
  const csv = bom + [header, ...rows].join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `usage-details-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/* ─── Narekening per regel ─── */

/**
 * Eén regel van de berekening: de formule, wat daar uitkomt, en wat de API gaf.
 *
 * Die laatste twee horen gelijk te zijn. Ze staan er beide omdat het verschil de
 * enige manier is om te zien dat de narekening ergens langs de berekening heen
 * loopt -- en dat is precies wat een beheerder hier komt controleren.
 */
function Bedragregel({
  label,
  formule,
  verwacht,
  gegeven,
}: {
  label: string;
  formule: string;
  verwacht: number | null;
  gegeven: number | null;
}) {
  const wijktAf =
    verwacht !== null && gegeven !== null && Math.abs(verwacht - gegeven) > AFRONDING;

  return (
    <div className="flex flex-wrap items-baseline gap-x-2">
      <span className="w-16 shrink-0 text-muted-foreground">{label}</span>
      <span className="font-mono">{formule}</span>
      <span className="font-mono font-medium">= {formatBedrag(gegeven)}</span>
      {wijktAf && (
        <span className="flex items-center gap-1 text-destructive">
          <AlertTriangle className="h-3.5 w-3.5" />
          narekening geeft {formatBedrag(verwacht)}
        </span>
      )}
    </div>
  );
}

/** Waarschuwing waarom er met nul is gerekend. */
function Ontbreekt({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-1.5 text-destructive">
      <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
      {children}
    </div>
  );
}

/** De periode waaruit de tarieven komen. */
function Periode({ tarieven }: { tarieven: VerbruikTarieven }) {
  if (!tarieven.billingPeriodId) {
    return (
      <Ontbreekt>
        Geen factuurperiode die deze datum dekt — er is met €0,00 gerekend. Zie
        Facturatie Stamgegevens.
      </Ontbreekt>
    );
  }

  return (
    <div className="flex flex-wrap items-baseline gap-x-2">
      <span className="w-16 shrink-0 text-muted-foreground">Periode</span>
      <span>
        {formatDatum(tarieven.billingPeriodStart)} t/m {formatDatum(tarieven.billingPeriodEnd)}
      </span>
    </div>
  );
}

/**
 * De berekening achter één verbruiksregel, uitgeschreven met de tarieven die de
 * API heeft gebruikt.
 *
 * Twee wegen, en welke van de twee geldt bepaalt de API (`pricing.basis`) en niet
 * dit scherm: spraak gaat per seconde tegen het tarief uit de factuurperiode, al
 * het andere rekent met de inkoopkosten die de AI-aanroep zelf meldde, met de
 * LLM-marge erop. Een telefoongesprek toont een duur maar loopt via die tweede
 * weg -- dat is te zien, en dat hoort ook zo.
 */
function Narekening({ log }: { log: DetailedLog }) {
  /**
   * Het veld staat in het type, maar niet in elk antwoord: een API die ouder is
   * dan dit scherm stuurt het niet mee. Dat mag geen witte pagina geven -- de
   * dev-server heeft geen foutoverlay (zie vite.config.ts), dus een uitzondering
   * in het uitklappen neemt de hele pagina mee en laat niets achter om aan te
   * zien wat er gebeurde.
   */
  const t = log.pricing as VerbruikTarieven | undefined;

  if (!t) {
    return (
      <Ontbreekt>
        Deze regel kwam zonder tarieven van de server, dus hier valt niets na te
        rekenen. Draait er nog een oudere versie van de API?
      </Ontbreekt>
    );
  }

  const prijs = (waarde: number) => `${formatBedrag(waarde)}/s`;
  const marge = t.llmMarginPercentage ?? 0;

  return (
    <div className="space-y-1.5 text-xs">
      <Periode tarieven={t} />

      {t.basis === 'seconds' ? (
        <>
          <Bedragregel
            label="Inkoop"
            formule={`${log.seconds} s × ${t.voicePurchasePricePerSecond === null ? '—' : prijs(t.voicePurchasePricePerSecond)}`}
            verwacht={
              t.voicePurchasePricePerSecond === null
                ? null
                : log.seconds * t.voicePurchasePricePerSecond
            }
            gegeven={log.purchaseCost}
          />
          <Bedragregel
            label="Verkoop"
            formule={`${log.seconds} s × ${t.voiceSellingPricePerSecond === null ? '—' : prijs(t.voiceSellingPricePerSecond)}`}
            verwacht={
              t.voiceSellingPricePerSecond === null
                ? null
                : log.seconds * t.voiceSellingPricePerSecond
            }
            gegeven={log.sellingCost}
          />
        </>
      ) : (
        <>
          {t.inputCost === null ? (
            <Ontbreekt>
              Deze aanroep meldde geen input_cost in de metadata — er is met €0,00
              gerekend.
            </Ontbreekt>
          ) : (
            <>
              <Bedragregel
                label="Inkoop"
                formule="input_cost uit de metadata"
                verwacht={t.inputCost}
                gegeven={log.purchaseCost}
              />
              <Bedragregel
                label="Verkoop"
                formule={`${formatBedrag(t.inputCost)} × (1 + ${marge
                  .toString()
                  .replace('.', ',')}% marge)`}
                verwacht={t.inputCost * (1 + marge / 100)}
                gegeven={log.sellingCost}
              />
            </>
          )}
        </>
      )}
    </div>
  );
}
